import { AuthConsumerRenderProps, withAuthContext } from "auth/AuthContext";
import axios, { CancelTokenSource } from "axios";
import CheckBox from "components/final-form/CheckBox";
import Input from "components/final-form/Input";
import TextArea from "components/final-form/TextArea";
import FormButtonsRow from "components/layout/FormButtonsRow";
import FormContainer from "components/layout/FormContainer";
import FormContent from "components/layout/FormContent";
import FormModalContentContainer from "components/layout/FormModalContentContainer";
import PraxFormGrid from "components/layout/PraxFormGrid";
import StyledErrorMessage from "components/layout/StyledErrorMessage";
import LoaderComponent from "components/LoaderComponent";
import { DoctorSearchDropdown } from "components/patient/upsert/DoctorSearchDropdown";
import ReportDiagnosisComponent from "components/patient/upsert/ReportDiagnosisComponent";
import { ReportDiagnosisDtoWithId } from "components/patient/upsert/UpsertPhysiotherapyReportModal";
import PatientReportPdfModal from "components/pdf-modal/PatientReportPdfModal";
import { FormApi, FormState } from "final-form";
import arrayMutators from "final-form-arrays";
import _ from "lodash";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { MessageSourceProps, withMessages } from "react-message-source";
import { Button, Grid, Icon, Message, Modal, Popup, Portal } from "semantic-ui-react";
import { getPdfDocumentUrl } from "services/patientRecordServices";
import { generatePatientSynopsisReport } from "services/patientServices";
import styled from "styled-components";
import { debounce } from "ts-debounce";
import { PatientDocumentType, SynopsisAbsence, SynopsisReasons } from "ts-types/api.enums";
import { DoctorDto, PatientDto, PatientSynopsisReportDto, PdfResponse } from "ts-types/api.types";
import { isoToDisplayDurationWithSeconds, isoToDisplayTimestamp, toIsoDateTimeString } from "utils/dateUtils";
import { errorUtils } from "utils/errorUtils";
import { generateUUID } from "utils/generatorUtils";

const ModalHeaderDiv = styled.div`
  & .person-icon {
    padding: 0 1.1rem 0 0.75rem;
  }
`;

const PopupContentDiv = styled.div`
  & {
    max-width: 71rem;
    white-space: pre-wrap;
  }
`;

const TimeColumn = styled(Grid.Column)`
  padding-left: 0 !important;
  padding-right: 0 !important;
  float: right;
`;

const PraxFormGridContainer = styled(PraxFormGrid)`
  padding-left: 0 !important;

  .ui.grid.side-table > .row {
    padding-top: 0.2rem !important;
    padding-bottom: 0.2rem !important;
  }

  .section-end {
    margin: 0 1rem 0.5rem;
    border-bottom: 1px dotted #bbb;
  }
`;

const TimerDiv = styled.div`
  position: fixed;
  top: 1rem;
  right: 1.5rem;
  background: white;
  z-index: 100000;
  font-size: medium;
  padding: 0.5rem;
  border-radius: 0.5rem;
`

const addButton = {
  marginBottom: 10,
  marginTop: 10
};

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
  modalOpen: boolean;
  onCloseModal: (savedDocument: boolean) => void;
  patient: PatientDto;
  treatmentId: number;
  reportDetails?: PatientSynopsisReportDto;
  documentId?: number;
}

interface PatientSynopsisReportDtoExtended extends PatientSynopsisReportDto {
  doctorInput: string | number | undefined;
  selectedSynopsisReasons: { [key in SynopsisReasons]: boolean };
  selectedDoneSynopsisReasons: { [key in SynopsisReasons]: boolean };
  selectedSynopsisAbsence: { [key in SynopsisAbsence]: boolean };
  selectedDoneSynopsisAbsence: { [key in SynopsisAbsence]: boolean };
}

interface PatientSynopsisReportFieldProps extends FormState {
  values: Partial<PatientSynopsisReportDtoExtended>;
}

const UpsertSynopsisReportModal = (props: Props) => {

  const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();

  const {
    modalOpen,
    onCloseModal, patient,
    treatmentId,
    reportDetails,
    documentId,
    getMessage,
    options,
    currentUser
  } = props;

  const [doctor, setDoctor] =
    useState(undefined as DoctorDto | string | undefined);
  const [doctorEmail, setDoctorEmail] = useState("");

  const [diagnoses, setDiagnoses] =
    useState([] as ReportDiagnosisDtoWithId[]);
  const [selectedDiagnoses, setSelectedDiagnoses] =
    useState([] as ReportDiagnosisDtoWithId[]);
  const [addDiagnose, setAddDiagnose] =
    useState(false);
  const [addDiagnoseIx, setAddDiagnoseIx] =
    useState(undefined as number | undefined);

  const [reportDto, setReportDto] =
    useState(undefined as Partial<PatientSynopsisReportDto> | undefined);
  const [patientSynopsisReportModalOpen, setPatientSynopsisReportModalOpen] =
    useState(false);
  const [patientSynopsisReportPdfResponse, setPatientSynopsisReportPdfResponse] =
    useState(undefined as PdfResponse | undefined);

  const [errorMessage, setErrorMessage] =
    useState(undefined as string | undefined);
  const [successfullySavedDocument, setSuccessfullySavedDocument] =
    useState(undefined as string | undefined);
  const [successfullySendEmail, setSuccessfullySendEmail] =
    useState(undefined as string | undefined);

  const [requiredDataLoaded, setRequiredDataLoaded] =
    useState(false);

  const [durationDisplay, setDurationDisplay] =
    useState(undefined as string | undefined);

  const [durationIntervalId, setDurationIntervalId] =
    useState(undefined as number | undefined);

  const initialValues = useRef<Partial<PatientSynopsisReportDtoExtended>>({});
  const formRef = useRef<FormApi>();
  useEffect(() => {
    const treaterName = currentUser && currentUser.treaterName;

    initialValues.current = {
      reportName: "Sprechstunde",
      treaterName: treaterName,
      requiresAction: true,
      abnormalFinding: false,
      discussWithPatient: false,
      startTime: toIsoDateTimeString(moment())
    };

    let selectedReasons: SynopsisReasons[] = [];
    let selectedReasonsProgress: SynopsisReasons[] = [];
    let selectedAbsences: SynopsisAbsence[] = [];
    let selectedAbsencesProgress: SynopsisAbsence[] = [];

    if (reportDetails) {
      const currentDiagnoses = reportDetails.diagnosis || [];
      const mappedDiagnoses = currentDiagnoses.map(diagnosis => ({
        ...diagnosis,
        id: generateUUID()
      }));

      initialValues.current = {
        ...reportDetails,
        reportName: reportDetails.reportName,
        diagnosis: mappedDiagnoses,
        doctorInput: reportDetails.doctor,
        requiresAction: reportDetails.requiresAction,
        abnormalFinding: reportDetails.abnormalFinding,
        discussWithPatient: reportDetails.discussWithPatient,
        startTime: reportDetails.startTime,
        endTime: reportDetails.endTime,
        duration: reportDetails.duration
      };

      selectedReasons = reportDetails.synopsisReasons as SynopsisReasons[] || [];
      selectedReasonsProgress = reportDetails.synopsisDoneReasons as SynopsisReasons[] || [];

      selectedAbsences = reportDetails.synopsisAbsences as SynopsisAbsence[] || [];
      selectedAbsencesProgress = reportDetails.synopsisDoneAbsences as SynopsisAbsence[] || [];

      setDiagnoses(mappedDiagnoses);
    }

    const reasons =
      _.mapValues(
        _.keyBy(
          Object.values(SynopsisReasons)),
        reason => !selectedReasons || selectedReasons.includes(reason));
    initialValues.current.selectedSynopsisReasons = reasons as any;

    const doneReasons =
      _.mapValues(
        _.keyBy(
          Object.values(SynopsisReasons)),
        reason => !selectedReasonsProgress || selectedReasonsProgress.includes(reason));
    initialValues.current.selectedDoneSynopsisReasons = doneReasons as any;


    const absences =
      _.mapValues(
        _.keyBy(
          Object.values(SynopsisAbsence)),
        absence => !selectedAbsences || selectedAbsences.includes(absence));
    initialValues.current.selectedSynopsisAbsence = absences as any;

    const doneAbsences =
      _.mapValues(
        _.keyBy(
          Object.values(SynopsisAbsence)),
        absence => !selectedAbsencesProgress || selectedAbsencesProgress.includes(absence));
    initialValues.current.selectedDoneSynopsisAbsence = doneAbsences as any;

    const fetchData = async () => {
      try {

        // await getDiagnosisForReport(patient.id, cancelTokenSource)
        //     .then(response => {
        //       initialValues = {
        //         diagnosis: response
        //       }
        //       setDiagnoses(response);
        //     })
        //     .catch((e: any) => handleError(e.response.data));

      } catch (e) {
        handleError(e);
      } finally {
        setRequiredDataLoaded(true);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (reportDetails) {
      // timer only enabled when adding a new document
      return;
    }

    startDurationTimer();

    return function cleanup() {
      if (durationIntervalId) {
        clearInterval(durationIntervalId);
      }
    };
  }, []);

  const startDurationTimer = () => {
    const intervalId = setInterval(() => {
      updateDuration();
    }, 1000);

    setDurationIntervalId(intervalId);
  };

  const stopDurationTimer = () => {
    if (!durationIntervalId) {
      return;
    }
    clearInterval(durationIntervalId);
    setDurationIntervalId(undefined);
  };

  const updateDuration = () => {
    const form = formRef.current;
    if (!form) {
      return;
    }

    const values = form.getState().values;
    const durationInSec = moment(values.startTime).diff(moment(), "seconds");
    const duration = moment.duration(durationInSec, "seconds");

    form.change("duration", duration.toISOString());
  };

  const handleError = (error: any) => {
    const errorCode = error.errorCode;
    const knownErrors: Array<String> = [
      errorUtils.unknownPatient,
      errorUtils.doctorNotFound
    ];
    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          setErrorMessage(getMessage(`error.${violation.errorCode}`));
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        setErrorMessage(getMessage(`error.${errorCode}`));
      } else {
        setErrorMessage(getMessage("error.general"));
      }
    }
  };

  const handleChange = debounce(({ values }: PatientSynopsisReportFieldProps) => {
    setDoctorEmail(values.doctorEmail ? values.doctorEmail : "");
    setDurationDisplay(isoToDisplayDurationWithSeconds(values.duration));
  });

  const openPatientSynopsisReport = (reportResponse: PdfResponse) => {
    setPatientSynopsisReportPdfResponse(reportResponse);
    setPatientSynopsisReportModalOpen(true);
  };

  const closePatientSynopsisReport = () => {
    setPatientSynopsisReportModalOpen(false);
    setPatientSynopsisReportPdfResponse(undefined);
  };

  const saveDocument = (successfullySaved: boolean) => {

    if (successfullySaved) {
      setSuccessfullySavedDocument(getMessage("synopsisReport.successfullySavedReport"));
    }

    closePatientSynopsisReport();
    onCloseModal(true);
  };

  const sendEmail = (successfullySendEmail: boolean) => {

    if (successfullySendEmail) {
      setSuccessfullySendEmail(getMessage("synopsisReport.successfullySendEmail"));
    }

    closePatientSynopsisReport();
    onCloseModal(true);
  };

  const getPatientDocumentPdfUrl = (pdfResponse: PdfResponse) => {
    return getPdfDocumentUrl(pdfResponse.pdfId, pdfResponse.pdfName);
  };

  const openSynopsisReport = async (
    values: Partial<PatientSynopsisReportDtoExtended>) => {

    stopDurationTimer();

    const selectedDoctor = values.doctorInput;
    let doc = selectedDoctor;
    if (doctor && typeof selectedDoctor === "number") {
      const doctorDto = doctor as DoctorDto;
      doc = `${doctorDto.firstName} ${doctorDto.name}`;
    }

    const reportValues = {
      ...values,
      reportName: values.reportName,
      doctor: doc as string,
      treaterId: options.currentTreater!.id,
      diagnosis: diagnoses.filter(d => d.selected),
      startTime: values.startTime ? values.startTime : toIsoDateTimeString(moment()),
      endTime: values.endTime ? values.endTime : toIsoDateTimeString(moment()),
      duration: values.duration,
      treatmentId: treatmentId
    };

    reportValues.synopsisReasons = [];
    if (values.selectedSynopsisReasons) {
      reportValues.synopsisReasons = Object.keys(values.selectedSynopsisReasons)
        .filter((srKey: string) => values.selectedSynopsisReasons
          ? values.selectedSynopsisReasons[srKey as SynopsisReasons]
          : false)
        .map((srKey: string) => srKey as SynopsisReasons);
    }

    reportValues.synopsisDoneReasons = [];
    if (reportValues.synopsisReasons && values.selectedDoneSynopsisReasons) {
      reportValues.synopsisDoneReasons = Object.keys(values.selectedDoneSynopsisReasons)
        .filter((srKey: string) => values.selectedDoneSynopsisReasons
        && reportValues.synopsisReasons
        && reportValues.synopsisReasons.includes(srKey as SynopsisReasons)
          ? values.selectedDoneSynopsisReasons[srKey as SynopsisReasons]
          : false)
        .map((srKey: string) => srKey as SynopsisReasons);
    }

    reportValues.synopsisAbsences = [];
    if (values.selectedSynopsisAbsence) {
      reportValues.synopsisAbsences = Object.keys(values.selectedSynopsisAbsence)
        .filter((saKey: string) => values.selectedSynopsisAbsence
          ? values.selectedSynopsisAbsence[saKey as SynopsisAbsence]
          : false)
        .map((srKey: string) => srKey as SynopsisAbsence);
    }

    reportValues.synopsisDoneAbsences = [];
    if (reportValues.synopsisAbsences && values.selectedDoneSynopsisAbsence) {
      reportValues.synopsisDoneAbsences = Object.keys(values.selectedDoneSynopsisAbsence)
        .filter((srKey: string) => values.selectedDoneSynopsisAbsence
        && reportValues.synopsisAbsences
        && reportValues.synopsisAbsences.includes(srKey as SynopsisAbsence)
          ? values.selectedDoneSynopsisAbsence[srKey as SynopsisAbsence]
          : false)
        .map((srKey: string) => srKey as SynopsisAbsence);
    }

    let synopsisReasonsComment = "";
    const synopsisReasons = reportValues.synopsisReasons;
    if (synopsisReasons
      && synopsisReasons.length > 0
      && synopsisReasons.includes("OTHER")
      && reportValues.synopsisReasonsComment) {

      synopsisReasonsComment = reportValues.synopsisReasonsComment;
    }

    let synopsisAbsencesComment = "";
    const synopsisAbsences = reportValues.synopsisAbsences;
    if (synopsisAbsences
      && synopsisAbsences.length > 0
      && synopsisAbsences.includes("OTHER")
      && reportValues.synopsisAbsencesComment) {

      synopsisAbsencesComment = reportValues.synopsisAbsencesComment;
    }

    const patientSynopsisReportDto: Partial<PatientSynopsisReportDto> = {
      doctor: reportValues.doctor,
      treaterName: reportValues.treaterName,
      doctorEmail: reportValues.doctorEmail,
      diagnosis: reportValues.diagnosis,
      currentProblem: reportValues.currentProblem,
      diagnosesText: reportValues.diagnosesText,
      problem: reportValues.problem,
      medicine: reportValues.medicine,
      anamnesis: reportValues.anamnesis,
      currentCondition: reportValues.currentCondition,
      procedure: reportValues.procedure,
      laboratoryResult: reportValues.laboratoryResult,
      comment: reportValues.comment,
      treatmentId: reportValues.treatmentId,
      treaterId: options.currentTreater.id,
      synopsisReasons: synopsisReasons,
      synopsisDoneReasons: reportValues.synopsisDoneReasons,
      synopsisReasonsComment: synopsisReasonsComment,
      synopsisAbsences: synopsisAbsences,
      synopsisDoneAbsences: reportValues.synopsisDoneAbsences,
      synopsisAbsencesComment: synopsisAbsencesComment,
      startTime: reportValues.startTime,
      endTime: reportValues.endTime,
      duration: reportValues.duration,
      requiresAction: values.requiresAction,
      abnormalFinding: values.abnormalFinding,
      discussWithPatient: values.discussWithPatient
    };

    setReportDto(patientSynopsisReportDto);

    generatePatientSynopsisReport(patient.id, reportValues, cancelTokenSource)
      .then(response => {
        openPatientSynopsisReport(response);
      })
      .catch((e: any) => handleError(e.response.data));
  };

  const addNewDiagnose = (fields: any, index: number) => {

    const newDiagnose: ReportDiagnosisDtoWithId = {
      id: generateUUID(),
      treatmentDate: moment().format("YYYY-MM-DD"),
      code: "",
      description: "",
      editable: true,
      hidden: false,
      selected: true
    };

    fields.push(newDiagnose);

    setAddDiagnose(true);
    setAddDiagnoseIx(index);
  };

  const unhiddeDiagnosis = (form: FormApi) => {

    const unhiddenDiagnosis: ReportDiagnosisDtoWithId[] = diagnoses.map(d => {
      return { ...d, hidden: false };
    });

    const modifiedDiagnoses = [...unhiddenDiagnosis];

    form.change("diagnosis", modifiedDiagnoses);

    setDiagnoses(modifiedDiagnoses);
  };

  const changeDiagnoses = (diagnoses: ReportDiagnosisDtoWithId[]) => {
    setDiagnoses(diagnoses);
  };

  const changeSelectedDiagnoses = (diagnoses: ReportDiagnosisDtoWithId[]) => {
    setSelectedDiagnoses(diagnoses);
  };

  const changeAddEditDiagnose = (addDiagnose: boolean) => {
    setAddDiagnose(addDiagnose);
  };

  const changeAddEditDiagnoseIx = (index?: number) => {
    setAddDiagnoseIx(index);
  };

  const renderModalHeader = () => {
    if (patient.doctorProblematic) {
      return <>
        <Icon className="person-icon" name="user circle" color="red" />
        <span className="perosn-name"
              style={{ color: "red" }}>{" " + patient.name + " " + patient.firstName + " "}</span>
        {
          patient.remark && patient.remark.trim().length > 0
          && <Popup
            trigger={<Icon name="exclamation circle" color="red" size="small" />}
            content={<PopupContentDiv>{patient.remark}</PopupContentDiv>}
            on="hover"
            size="small"
            position="bottom center"
          />
        }
      </>;
    } else {
      return <>
        <Icon name="user circle" /> {" " + patient.name + " " + patient.firstName + " "}
      </>;
    }
  };

  const renderDiagnosesSegment = (form: FormApi): JSX.Element => {

    return (
      <ReportDiagnosisComponent
        diagnoses={diagnoses}
        addDiagnose={addDiagnose}
        addDiagnoseIx={addDiagnoseIx}
        changeDiagnoses={changeDiagnoses}
        changeSelectedDiagnoses={changeSelectedDiagnoses}
        changeAddEditDiagnose={changeAddEditDiagnose}
        changeAddEditDiagnoseIx={changeAddEditDiagnoseIx}
        formChange={form.change}
      />
    );
  };

  const onFinishButtonClicked = (form: FormApi) => {
    stopDurationTimer();
    form.change("endTime", toIsoDateTimeString(moment()));
  };

  const handleActionsChange = (values: any, form: FormApi, fieldName: string) => {
    const currentValues = form.getState().values;

    const selectedSynopsisReasons = currentValues.selectedSynopsisReasons;

    const existsSelectedNotDoneReason = Object.values(SynopsisReasons).findIndex(reason =>
      selectedSynopsisReasons[reason]
      && !currentValues.selectedDoneSynopsisReasons[reason]) >= 0;

    const selectedSynopsisAbsence = currentValues.selectedSynopsisAbsence;

    const existsSelectedNotDoneAbsence = Object.values(SynopsisAbsence).findIndex(reason =>
      selectedSynopsisAbsence[reason]
      && !currentValues.selectedDoneSynopsisAbsence[reason]) >= 0;

    let requiresAction = existsSelectedNotDoneReason || existsSelectedNotDoneAbsence;
    form.change("requiresAction", requiresAction);
  };

  const renderSynopsisReasonsSection = (values: any, form: FormApi): JSX.Element => {
    const mappedFields = () => {
      const fieldName = Object.values(SynopsisReasons);

      return fieldName.map((fieldName, index) => (
        <Grid.Row key={index}>
          <Grid.Column width={4}>
            <Field
              name={`selectedSynopsisReasons[${fieldName}]`}
              component={CheckBox}
              label={getMessage(`enum.synopsisReasons.${fieldName}`)}
              customOnChange={() => handleActionsChange(values, form, fieldName)}
            />
            {
              values.selectedSynopsisReasons
              && values.selectedSynopsisReasons[fieldName]
              && (<Field
                  name={`selectedDoneSynopsisReasons[${fieldName}]`}
                  component={CheckBox}
                  style={{ float: "right", verticalAlign: "middle" }}
                  customOnChange={() => handleActionsChange(values, form, fieldName)}
                />
              )

            }
          </Grid.Column>
          {
            fieldName === SynopsisReasons.OTHER
            && values.selectedSynopsisReasons
            && values.selectedSynopsisReasons[SynopsisReasons.OTHER]
            && (
              <Grid.Column style={{ width: "100%" }}>
                <Field
                  name={"synopsisReasonsComment"}
                  component={TextArea}
                  style={{ width: "100%" }}
                />
              </Grid.Column>
            )
          }
        </Grid.Row>
      ));
    };

    return (
      <React.Fragment>
        <Grid.Row>
          <Grid.Column width={10} style={{ fontWeight: "bold" }}>
            {getMessage("patientCentral.overview.tableHeader.rowActions")}
            <span style={{ float: "right" }}>
                {getMessage("synopsisReport.modal.done")}
              </span>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={10}>
            {mappedFields()}
          </Grid.Column>
        </Grid.Row>
      </React.Fragment>
    );
  };

  const renderSynopsisAbsenceSection = (values: any, form: FormApi): JSX.Element => {


    const mappedFields = () => {
      const fieldName = Object.values(SynopsisAbsence);

      return fieldName.map((fieldName, index) => (
        <Grid.Row key={index}>
          <Grid.Column width={4}>
            <Field
              name={`selectedSynopsisAbsence[${fieldName}]`}
              component={CheckBox}
              label={getMessage(`enum.synopsisAbsence.${fieldName}`)}
              customOnChange={() => handleActionsChange(values, form, fieldName)}
            />
            {
              values.selectedSynopsisAbsence
              && values.selectedSynopsisAbsence[fieldName]
              && (<Field
                  name={`selectedDoneSynopsisAbsence[${fieldName}]`}
                  component={CheckBox}
                  style={{ float: "right", verticalAlign: "middle" }}
                  customOnChange={() => handleActionsChange(values, form, fieldName)}
                  value={!!(values.selectedDoneSynopsisAbsence && values.selectedDoneSynopsisAbsence[fieldName])}
                />
              )

            }
          </Grid.Column>
          {
            fieldName === SynopsisAbsence.OTHER
            && values.selectedSynopsisAbsence
            && values.selectedSynopsisAbsence[SynopsisAbsence.OTHER]
            && (
              <Grid.Column style={{ width: "100%" }}>
                <Field
                  name={"synopsisAbsencesComment"}
                  component={TextArea}
                  style={{ width: "100%" }}
                />
              </Grid.Column>
            )
          }
        </Grid.Row>
      ));
    };

    return (
      <React.Fragment>
        <Grid.Row>
          <Grid.Column width={2} style={{ fontWeight: "bold" }}>
            {getMessage("synopsisReport.modal.absence")}
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={10}>
            {mappedFields()}
          </Grid.Column>
        </Grid.Row>
      </React.Fragment>
    );
  };

  const renderForm = ({ form, submitting, handleSubmit }: FormRenderProps): JSX.Element => {

    const disableUnhiddeButton = diagnoses.filter(d => d.hidden).length === 0;

    if (!formRef.current) {
      formRef.current = form;
    }

    const formValues = form.getState().values;

    const startTimeDisplay = isoToDisplayTimestamp(formValues.startTime);

    let endTimeDisplay = isoToDisplayTimestamp(formValues.endTime);

    let durationDisplay = isoToDisplayDurationWithSeconds(formValues.duration);

    return (
      <React.Fragment>
        <form onSubmit={handleSubmit}>
          {
            errorMessage
            && (
              <StyledErrorMessage onDismiss={() => setErrorMessage(undefined)}>
                {errorMessage}
              </StyledErrorMessage>
            )
          }
          {
            successfullySavedDocument
            && (
              <Message success onDismiss={() => setSuccessfullySavedDocument(undefined)}>
                {successfullySavedDocument}
              </Message>
            )

          }
          {
            successfullySendEmail
            && (
              <Message success onDismiss={() => setSuccessfullySendEmail(undefined)}>
                {successfullySendEmail}
              </Message>
            )

          }
          <FormContainer>
            <FormContent>
              <PraxFormGridContainer style={{ marginBottom: 0 }}>
                <Grid.Row>
                  <Grid.Column width={10}>
                    <PraxFormGrid>
                      <Grid.Row>
                        <Grid.Column width={4}>
                          {getMessage("interventionOrderItem.reportNamePrefix")}:
                        </Grid.Column>
                        <Grid.Column width={12}>
                          <Field
                            name="reportName"
                            component={Input}
                            fluid
                          />
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={4}>
                          {getMessage("treatment.info.treater")}:
                        </Grid.Column>
                        <Grid.Column width={12}>
                          <Field
                            name="treaterName"
                            component={Input}
                            fluid
                          />
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={4}>
                          {getMessage("doctor")}:
                        </Grid.Column>
                        <Grid.Column width={12}>
                          <Field
                            name="doctorInput"
                            component={DoctorSearchDropdown}
                            defaultValue={initialValues.current.doctor}
                            onDoctorChange={(doctor: DoctorDto | string | undefined) => {

                              setDoctor(doctor);

                              let email = "";
                              if (doctor && typeof doctor !== "string") {
                                email = (doctor as DoctorDto).email;
                              }
                              form.change("doctorEmail", email);
                            }}
                            fluid
                          />
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={4}>
                          {getMessage("synopsisReport.modal.doctorEmail")}:
                        </Grid.Column>
                        <Grid.Column width={12}>
                          <Field
                            name="doctorEmail"
                            component={Input}
                            fluid
                          />
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={4} style={{ fontWeight: "bold" }}>
                          {getMessage("synopsisReport.modal.aktuellesProblem")}
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={16}>
                          <Field
                            name="currentProblem"
                            component={TextArea}
                            fluid
                          />
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={4} style={{ fontWeight: "bold" }}>
                          {getMessage("synopsisReport.modal.problem")}
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={16}>
                          <Field
                            name="problem"
                            component={TextArea}
                            fluid
                          />
                        </Grid.Column>
                      </Grid.Row>
                    </PraxFormGrid>
                  </Grid.Column>

                  <Grid.Column width={6}>
                    <PraxFormGrid className="side-table">
                      <Grid.Row>
                        <Grid.Column style={{ display: "flex", flexDirection: "row" }} width={16}>
                          <div style={{ display: "flex", flexDirection: "row", flex: "1 1 auto" }} />
                          <div style={{ display: "flex", flexDirection: "column", flex: "0 0 auto" }}>
                            <Field
                              name="requiresAction"
                              component={CheckBox}
                              label={getMessage("synopsisReport.modal.requiredAction")}
                            />
                            <Field
                              name="abnormalFinding"
                              component={CheckBox}
                              label={getMessage("patientRecord.patientDocument.abnormalFinding")}
                            />
                            <Field
                              name="discussWithPatient"
                              component={CheckBox}
                              label={getMessage("patientRecord.patientDocument.discussWithPatient")}
                            />
                          </div>
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row className="section-end">
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={6}>
                          <div style={{ float: "right" }}>
                            {getMessage("synopsisReport.modal.startTime")}
                          </div>
                        </Grid.Column>
                        <TimeColumn width={9}>
                          <span style={{ float: "right" }}>{startTimeDisplay}</span>
                        </TimeColumn>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={6}>
                          <div style={{ float: "right" }}>
                            {getMessage("synopsisReport.modal.endTime")}
                          </div>
                        </Grid.Column>
                        <TimeColumn width={9}>
                          <span style={{ float: "right" }}>{endTimeDisplay}</span>
                        </TimeColumn>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={6}>
                          <div style={{ float: "right" }}>
                            {getMessage("synopsisReport.modal.duration")}
                          </div>
                        </Grid.Column>
                        <TimeColumn width={9}>
                          <span style={{ float: "right" }}>{durationDisplay}</span>
                        </TimeColumn>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={6}></Grid.Column>
                        <Grid.Column width={9} style={{ paddingRight: 0 }}>
                          {
                            durationIntervalId
                            && <Button type="button"
                                       compact
                                       size="small"
                                       floated="right"
                                       disabled={submitting}
                                       onClick={() => onFinishButtonClicked(form)}
                            >
                              {getMessage("patientCentral.patientEntry.actions.finish")}
                            </Button>
                          }
                          {
                            !durationIntervalId && !reportDetails
                            && <Button type="button"
                                       compact
                                       size="small"
                                       floated="right"
                                       disabled={submitting}
                                       onClick={() => startDurationTimer()}
                            >
                              {getMessage("patientCentral.patientEntry.actions.resume")}
                            </Button>
                          }
                        </Grid.Column>
                      </Grid.Row>
                    </PraxFormGrid>
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGridContainer>
              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column width={8} style={{ fontWeight: "bold" }}>
                    {getMessage("synopsisReport.modal.diagnosis")}
                  </Grid.Column>
                  <Grid.Column width={8}>
                    <Button
                      type="button"
                      compact
                      disabled={disableUnhiddeButton}
                      onClick={() => unhiddeDiagnosis(form)}
                      style={{ float: "right" }}
                    >
                      {getMessage("synopsisReport.modal.unhiddeButton")}
                    </Button>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <div style={{ width: "100%" }}>
                      {renderDiagnosesSegment(form)}
                    </div>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column>
                    <FieldArray name="diagnosis">
                      {({ fields }) => (
                        <Button
                          type="button"
                          compact
                          style={addButton}
                          onClick={() => addNewDiagnose(fields, fields.length!)}
                        >
                          {getMessage("button.add")}
                        </Button>
                      )}
                    </FieldArray>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={3} style={{ fontWeight: "bold" }}>
                    {getMessage("synopsisReport.modal.diagnosis")}
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={10}>
                    <Field
                      name="diagnosesText"
                      component={TextArea}
                      fluid
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={2} style={{ fontWeight: "bold" }}>
                    {getMessage("synopsisReport.modal.medicine")}
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={10}>
                    <Field
                      name="medicine"
                      component={TextArea}
                      fluid
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={2} style={{ fontWeight: "bold" }}>
                    {getMessage("synopsisReport.modal.anamnesis")}:
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={10}>
                    <Field
                      name="anamnesis"
                      component={TextArea}
                      fluid
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={3} style={{ fontWeight: "bold" }}>
                    {getMessage("synopsisReport.modal.currentCondition")}
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={10}>
                    <Field
                      name="currentCondition"
                      component={TextArea}
                      fluid
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={2} style={{ fontWeight: "bold" }}>
                    {getMessage("synopsisReport.modal.procedure")}
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={10}>
                    <Field
                      name="procedure"
                      component={TextArea}
                      fluid
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={2} style={{ fontWeight: "bold" }}>
                    {getMessage("synopsisReport.modal.labor")}:
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={10}>
                    <Field
                      name="laboratoryResult"
                      component={TextArea}
                      fluid
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={2} style={{ fontWeight: "bold" }}>
                    {getMessage("synopsisReport.modal.comment")}
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={10}>
                    <Field
                      name="comment"
                      component={TextArea}
                      fluid
                    />
                  </Grid.Column>
                </Grid.Row>
                {renderSynopsisReasonsSection(formValues, form)}
                {renderSynopsisAbsenceSection(formValues, form)}
              </PraxFormGrid>
            </FormContent>

            <FormButtonsRow>
              <Button compact
                      primary
                      disabled={submitting}
              >
                {getMessage("synopsisReport.modal.generateReport.button")}
              </Button>
              <Button type="button"
                      compact
                      disabled={submitting}
                      onClick={() => onCloseModal(false)}>
                {getMessage("button.cancel")}
              </Button>
            </FormButtonsRow>
          </FormContainer>

          <FormSpy subscription={{ values: true }} onChange={handleChange} />

          {
            patientSynopsisReportModalOpen
            && <PatientReportPdfModal
              pdfResponses={[patientSynopsisReportPdfResponse]}
              modalOpen={patientSynopsisReportModalOpen}
              onSave={saveDocument}
              onSendEmail={sendEmail}
              onCloseModal={closePatientSynopsisReport}
              getPdfUrl={getPatientDocumentPdfUrl}
              sendEmailTextKey={getMessage("synopsisReport.pdfModal.email.send")}
              doctorEmail={doctorEmail}
              patient={patient}
              pdfDocumentDescription={"Patient Synopsis Report"}
              documentType={PatientDocumentType.PATIENT_SYNOPSIS_REPORT}
              treatmentId={treatmentId}
              reportDto={reportDto}
              reportId={documentId}
            />
          }
        </form>
      </React.Fragment>
    );
  };

  return (
    <>
      <Modal
        open={modalOpen}
        onClose={() => onCloseModal(false)}
        closeOnDimmerClick={false}
      >
        <Modal.Header>
          <ModalHeaderDiv>
            {getMessage("synopsisReport.modal.header")}
            {renderModalHeader()}
          </ModalHeaderDiv>
        </Modal.Header>
        <FormModalContentContainer>
          {!requiredDataLoaded
            ? <LoaderComponent message={getMessage("loading")} />
            : <FinalForm
              onSubmit={(values) => openSynopsisReport(values)}
              subscription={{ submitting: true, pristine: true, values: true }}
              initialValues={initialValues.current}
              mutators={{
                ...arrayMutators
              }}
              render={renderForm}
            />
          }
        </FormModalContentContainer>
      </Modal>
      {
        requiredDataLoaded &&
        <Portal open={true}>
          <TimerDiv>{durationDisplay}</TimerDiv>
        </Portal>
      }
    </>
  );
};

export default withMessages(withAuthContext(UpsertSynopsisReportModal));
